/**
 * Token refresh module for automatic JWT renewal.
 *
 * Provides two mechanisms:
 * - {@link refreshServicemanToken} — Manual refresh with deduplication (concurrent calls share one promise)
 * - {@link setupTokenRefresh} — Automatic refresh on page load, window focus, and visibility change
 *
 * @remarks
 * Uses raw `axios` (not `api/axios.ts`) to avoid interceptor loops.
 * Backend: `POST /serviceman/auth/refresh-token` with `ServicemanTokenMiddlewareForRefresh`
 * which accepts expired tokens for renewal.
 *
 * Error handling:
 * - 403 (access revoked) → clears auth, redirects to `/logout`
 * - 401 (invalid token) → clears auth, redirects to `/`
 * - Other errors → returns null, auth state unchanged
 *
 * Throttle: Maximum one refresh per 30 seconds to avoid hammering the backend.
 *
 * @module libs/tokenRefresh
 */
import axios from 'axios'

const isDev = process.env.NODE_ENV === 'development'
const baseURL = isDev ? 'http://localhost:9083' : '/api'

let refreshInProgress = false
let refreshPromise: Promise<string | null> | null = null

/**
 * Refreshes the JWT token via the backend refresh endpoint.
 *
 * Implements promise-based deduplication: if a refresh is already in progress,
 * subsequent calls return the same promise instead of firing multiple requests.
 *
 * @remarks
 * Backend: `POST /serviceman/auth/refresh-token` with Bearer token header.
 * The refresh endpoint uses `ServicemanTokenMiddlewareForRefresh` which allows
 * expired tokens, enabling seamless renewal before the user notices.
 *
 * @returns The new JWT token string on success, or `null` on failure
 */
export async function refreshServicemanToken(): Promise<string | null> {
  // If a refresh is already in progress, return the existing promise
  if (refreshInProgress && refreshPromise) {
    return refreshPromise
  }

  refreshInProgress = true
  refreshPromise = (async () => {
    try {
      const currentToken = localStorage.getItem('token')

      if (!currentToken) {
        console.warn('No token found, skipping refresh')
        return null
      }

      const response = await axios.post(
        `${baseURL}/serviceman/auth/refresh-token`,
        {},
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
          timeout: 10000,
        }
      )

      if (response.status === 200 && response.data.token) {
        const newToken = response.data.token
        localStorage.setItem('token', newToken)
        return newToken
      }

      return null
    } catch (error) {
      // Type guard for AxiosError
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 403) {
          // Access revoked - user no longer has permissions
          console.error('Access revoked, logging out')
          localStorage.removeItem('token')
          localStorage.removeItem('email')
          localStorage.removeItem('last_env')
          window.location.href = '/logout'
          return null
        }

        if (error.response?.status === 401) {
          // Token expired or invalid
          console.error('Token invalid, logging out')
          localStorage.removeItem('token')
          localStorage.removeItem('email')
          localStorage.removeItem('last_env')
          window.location.href = '/'
          return null
        }

        console.error('Token refresh failed:', error.message)
      } else {
        console.error('Token refresh failed:', error)
      }

      return null
    } finally {
      refreshInProgress = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

/**
 * Sets up automatic token refresh on page load, window focus, and tab visibility change.
 *
 * Registers event listeners for `focus` and `visibilitychange` events.
 * Throttled to at most one refresh per 30 seconds to prevent excessive API calls.
 * Only refreshes when a token exists in localStorage.
 */
export function setupTokenRefresh(): void {
  // Refresh on page load
  if (localStorage.getItem('token')) {
    refreshServicemanToken().catch(err => {
      console.error('Initial token refresh failed:', err)
    })
  }

  // Refresh when user returns to the tab/window
  let lastRefresh = Date.now()
  const REFRESH_THROTTLE_MS = 30000 // Only refresh once per 30 seconds

  window.addEventListener('focus', () => {
    const now = Date.now()
    if (now - lastRefresh > REFRESH_THROTTLE_MS && localStorage.getItem('token')) {
      lastRefresh = now
      refreshServicemanToken().catch(err => {
        console.error('Token refresh on focus failed:', err)
      })
    }
  })

  // Optionally refresh on visibility change (when tab becomes visible)
  document.addEventListener('visibilitychange', () => {
    const now = Date.now()
    if (!document.hidden && now - lastRefresh > REFRESH_THROTTLE_MS && localStorage.getItem('token')) {
      lastRefresh = now
      refreshServicemanToken().catch(err => {
        console.error('Token refresh on visibility change failed:', err)
      })
    }
  })
}
